import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../config/Api';
import { Product, ProductStatus } from '../../types/productTypes';

interface AdminProductState {
  pendingProducts: Product[];
  approvedProducts: Product[];
  rejectedProducts: Product[];
  totalPendingPages: number;
  totalApprovedPages: number;
  totalRejectedPages: number;
  loading: boolean;
  error: string | null;
}

const initialState: AdminProductState = {
  pendingProducts: [],
  approvedProducts: [],
  rejectedProducts: [],
  totalPendingPages: 0,
  totalApprovedPages: 0,
  totalRejectedPages: 0,
  loading: false,
  error: null,
};

// Helper function to sort products by createdAt descending (newest first)
const sortByNewest = (products: Product[]): Product[] => {
  return [...products].sort((a, b) => {
    const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return timeB - timeA;
  });
};

export const fetchAdminProducts = createAsyncThunk(
  'adminProduct/fetchAdminProducts',
  async ({ status, page }: { status: ProductStatus; page: number }, { rejectWithValue }) => {
    try {
      // Updated endpoint URL to /api/admin/products/status
      const response = await api.get(
        `/api/admin/products/status?status=${status}&page=${page}&size=30&sort=createdAt,desc`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` } }
      );
      return { data: response.data, status };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch products');
    }
  }
);

export const updateApprovalStatus = createAsyncThunk(
  'adminProduct/updateApprovalStatus',
  async (
    { productId, status, comment }: { productId: number; status: ProductStatus; comment?: string | null },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.patch(
        `/api/admin/products/${productId}/approval`,
        { status, comment: comment || null },
        { headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` } }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update status');
    }
  }
);

const adminProductSlice = createSlice({
  name: 'adminProduct',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminProducts.fulfilled, (state, action) => {
        state.loading = false;
        const rawData = action.payload.data;
        const productsList: Product[] = Array.isArray(rawData) ? rawData : rawData?.content || [];
        const totalPages = rawData?.totalPages || 1;

        const sortedProducts = sortByNewest(productsList);

        switch (action.payload.status) {
          case 'APPROVED':
            state.approvedProducts = sortedProducts;
            state.totalApprovedPages = totalPages;
            break;
          case 'REJECTED':
            state.rejectedProducts = sortedProducts;
            state.totalRejectedPages = totalPages;
            break;
          case 'PENDING':
          default:
            state.pendingProducts = sortedProducts;
            state.totalPendingPages = totalPages;
            break;
        }
      })
      .addCase(fetchAdminProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateApprovalStatus.fulfilled, (state, action) => {
        const updatedProduct: Product = action.payload;

        state.pendingProducts = state.pendingProducts.filter((p) => p.id !== updatedProduct.id);
        state.approvedProducts = state.approvedProducts.filter((p) => p.id !== updatedProduct.id);
        state.rejectedProducts = state.rejectedProducts.filter((p) => p.id !== updatedProduct.id);

        if (updatedProduct.status === 'APPROVED' || updatedProduct.isApproved) {
          state.approvedProducts = [updatedProduct, ...state.approvedProducts];
        } else if (updatedProduct.status === 'REJECTED') {
          state.rejectedProducts = [updatedProduct, ...state.rejectedProducts];
        } else {
          state.pendingProducts = [updatedProduct, ...state.pendingProducts];
        }
      });
  },
});

export default adminProductSlice.reducer;